import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { savingsAccountsApi, savingsProductsApi, clientsApi } from '../lib/api';
import api from '../lib/api';
import { formatDate, formatCurrency, getStatusColor, capitalizeFirst, formatPercentage } from '../lib/utils';
import { toast } from 'sonner';
import {
  PiggyBank, Plus, Search, Filter, MoreHorizontal, Eye, ArrowUpCircle, ArrowDownCircle,
  ChevronLeft, ChevronRight, FileText, ArrowLeftRight, Download, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SavingsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const officeId = searchParams.get('office_id');
  const officeName = searchParams.get('office_name');

  useEffect(() => {
    if (searchParams.get('action') === 'new') setShowNewModal(true);
  }, [searchParams]);

  useEffect(() => {
    loadAccounts();
  }, [page, statusFilter, officeId]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const params = { skip: page * 20, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (officeId) params.office_id = officeId;
      const response = await savingsAccountsApi.list(params);
      setAccounts(response.data.accounts);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewAccount = async (accountId) => {
    try {
      const response = await savingsAccountsApi.get(accountId);
      setSelectedAccount(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load account:', error);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="savings-page">
      {/* Office Filter Banner */}
      {officeId && (
        <div className="bg-[#1f1f1f]/5 border border-[#1f1f1f]/10 px-4 py-2 flex items-center justify-between" data-testid="office-filter-banner">
          <span className="text-sm text-[#1f1f1f]">Filtered by office: <strong>{decodeURIComponent(officeName || 'Selected Office')}</strong></span>
          <div className="flex gap-2">
            <button onClick={() => navigate(`/dashboard/clients?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Clients</button>
            <button onClick={() => navigate(`/dashboard/loans?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Loans</button>
            <button onClick={() => navigate('/dashboard/savings')} className="text-xs text-red-500 hover:underline">Clear Filter</button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            DEPOSIT MANAGEMENT
          </p>
          <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
            Savings Accounts
          </h1>
        </div>
        <Button
          onClick={() => setShowNewModal(true)}
          className="bg-[#1f1f1f] hover:bg-[#333333]"
          data-testid="new-account-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Account
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 border border-gray-200 bg-[#1f1f1f] text-white">
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Total Deposits</p>
          <p className="text-2xl font-bold">
            {formatCurrency(accounts.reduce((sum, a) => sum + (a.balance || 0), 0))}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Active Accounts</p>
          <p className="text-2xl font-bold text-emerald-600">
            {accounts.filter(a => a.status === 'active').length}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Savings</p>
          <p className="text-2xl font-bold text-[#1f1f1f]">
            {accounts.filter(a => a.account_type === 'savings').length}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Fixed Deposits</p>
          <p className="text-2xl font-bold text-[#C5A059]">
            {accounts.filter(a => a.account_type === 'fixed_deposit').length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="savings-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Interest Rate
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No accounts found
                  </td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50" data-testid={`account-row-${account.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{account.account_number}</p>
                      <p className="text-sm text-gray-500">{formatDate(account.created_at)}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {account.client_name}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        {capitalizeFirst(account.account_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(account.balance)}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-right">
                      {formatPercentage(account.interest_rate)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${getStatusColor(account.status)}`}>
                        {capitalizeFirst(account.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`account-actions-${account.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewAccount(account.id)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { viewAccount(account.id); }}>
                            <ArrowUpCircle className="w-4 h-4 mr-2" /> Deposit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { viewAccount(account.id); }}>
                            <ArrowDownCircle className="w-4 h-4 mr-2" /> Withdraw
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {Math.min(page * 20 + 1, total)} to {Math.min((page + 1) * 20, total)} of {total} accounts
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* New Account Modal */}
      <NewAccountModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={() => {
          setShowNewModal(false);
          loadAccounts();
        }}
      />

      {/* Account Detail Modal */}
      <AccountDetailModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        account={selectedAccount}
        onRefresh={() => viewAccount(selectedAccount?.id)}
      />
    </div>
  );
};

const NewAccountModal = ({ open, onClose, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '', product_id: '', initial_deposit: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadProducts();
      loadClients();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      const response = await savingsProductsApi.list();
      setProducts(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsApi.list({ status: 'active', limit: 100 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      initial_deposit: product?.min_opening_balance || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        ...formData,
        initial_deposit: parseFloat(formData.initial_deposit) || 0
      };
      await savingsAccountsApi.create(data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Open New Account</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Client *</Label>
            <Select value={formData.client_id} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
              <SelectTrigger data-testid="account-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} ({c.client_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Account Type *</Label>
            <Select value={formData.product_id} onValueChange={handleProductChange}>
              <SelectTrigger data-testid="account-product-select">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                {products.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} ({formatPercentage(p.interest_rate)} p.a.)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProduct && (
            <div className="p-4 bg-gray-50 border border-gray-200 text-sm space-y-2">
              <p className="font-medium text-gray-900">{selectedProduct.name}</p>
              <div className="grid grid-cols-2 gap-4 text-gray-600">
                <div>
                  <span className="text-gray-500">Min Opening Balance:</span>
                  <br />{formatCurrency(selectedProduct.min_opening_balance)}
                </div>
                <div>
                  <span className="text-gray-500">Interest Rate:</span>
                  <br />{formatPercentage(selectedProduct.interest_rate)} p.a.
                </div>
              </div>
              {selectedProduct.lock_in_period_days > 0 && (
                <p className="text-amber-600">
                  Lock-in period: {selectedProduct.lock_in_period_days} days
                </p>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Initial Deposit (ZAR)</Label>
            <Input
              type="number"
              value={formData.initial_deposit}
              onChange={(e) => setFormData(prev => ({ ...prev, initial_deposit: e.target.value }))}
              placeholder="0.00"
              data-testid="initial-deposit-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-new-account">
              {loading ? 'Creating...' : 'Open Account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AccountDetailModal = ({ open, onClose, account, onRefresh }) => {
  const [showTransaction, setShowTransaction] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('cash');
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(null); // 'internal' or 'external'
  const [transferData, setTransferData] = useState({
    to_account_id: '', amount: '', description: '',
    beneficiary_name: '', beneficiary_bank: '', beneficiary_account_number: '', beneficiary_branch_code: '',
  });
  const [transferLoading, setTransferLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);

  React.useEffect(() => {
    if (showTransfer === 'internal' && account) {
      savingsAccountsApi.list({ limit: 100 }).then(res => {
        setAllAccounts(res.data.accounts.filter(a => a.id !== account.id && a.status === 'active'));
      }).catch(() => {});
    }
  }, [showTransfer, account]);

  React.useEffect(() => {
    api.get('/payment-types').then(res => setPaymentTypes(res.data || [])).catch(() => {});
  }, []);

  if (!account) return null;

  const handleTransaction = async (type) => {
    setTransactionLoading(true);
    try {
      if (type === 'deposit') {
        await savingsAccountsApi.deposit(account.id, { amount: parseFloat(amount), payment_type: paymentType });
      } else {
        await savingsAccountsApi.withdraw(account.id, { amount: parseFloat(amount), payment_type: paymentType });
      }
      setShowTransaction(null);
      setAmount('');
      setPaymentType('cash');
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${type}`);
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleDownloadStatement = async (format) => {
    try {
      const endpoint = format === 'csv'
        ? `/savings-accounts/${account.id}/statement/csv`
        : `/savings-accounts/${account.id}/statement`;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_${account.account_number}_${new Date().toISOString().slice(0,10)}.${format === 'csv' ? 'csv' : 'pdf'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} statement downloaded`);
    } catch (e) {
      toast.error('Failed to download statement');
    }
  };

  const handleTransfer = async () => {
    setTransferLoading(true);
    try {
      if (showTransfer === 'internal') {
        const res = await api.post('/transfers/internal', {
          from_account_id: account.id,
          to_account_id: transferData.to_account_id,
          amount: parseFloat(transferData.amount),
          description: transferData.description || 'Internal transfer',
        });
        toast.success(`Transfer successful. Ref: ${res.data.reference}`);
      } else {
        const res = await api.post('/transfers/external', {
          from_account_id: account.id,
          amount: parseFloat(transferData.amount),
          beneficiary_name: transferData.beneficiary_name,
          beneficiary_bank: transferData.beneficiary_bank,
          beneficiary_account_number: transferData.beneficiary_account_number,
          beneficiary_branch_code: transferData.beneficiary_branch_code,
          description: transferData.description || 'External transfer',
        });
        toast.info(res.data.message);
      }
      setShowTransfer(null);
      setTransferData({ to_account_id: '', amount: '', description: '', beneficiary_name: '', beneficiary_bank: '', beneficiary_account_number: '', beneficiary_branch_code: '' });
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    }
    setTransferLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-3">
            <PiggyBank className="w-6 h-6 text-[#C5A059]" />
            <div>
              <p>{account.account_number}</p>
              <p className="text-sm font-normal text-gray-500">{capitalizeFirst(account.account_type)}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Balance Card */}
          <div className="p-6 bg-[#1f1f1f] text-white">
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Available Balance</p>
            <p className="text-4xl font-bold font-display">{formatCurrency(account.balance)}</p>
            <div className="flex gap-4 mt-4">
              <Button
                onClick={() => setShowTransaction('deposit')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="deposit-button"
              >
                <ArrowUpCircle className="w-4 h-4 mr-2" /> Deposit
              </Button>
              <Button
                onClick={() => setShowTransaction('withdraw')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="withdraw-button"
              >
                <ArrowDownCircle className="w-4 h-4 mr-2" /> Withdraw
              </Button>
              <Button
                onClick={() => setShowTransfer('internal')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="transfer-button"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="statement-button">
                    <FileText className="w-4 h-4 mr-2" /> Statement
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleDownloadStatement('pdf')} data-testid="statement-pdf">
                    <Download className="w-3.5 h-3.5 mr-2" />Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownloadStatement('csv')} data-testid="statement-csv">
                    <Download className="w-3.5 h-3.5 mr-2" />Download CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Account Details */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Account Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Interest Rate</span>
                  <span className="font-medium">{formatPercentage(account.interest_rate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Accrued Interest</span>
                  <span className="font-medium">{formatCurrency(account.accrued_interest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span className={`px-2 py-0.5 text-xs ${getStatusColor(account.status)}`}>
                    {capitalizeFirst(account.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Deposits</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(account.total_deposits)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Withdrawals</span>
                  <span className="font-medium text-red-600">{formatCurrency(account.total_withdrawals)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Opened</span>
                  <span className="font-medium">{formatDate(account.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction History */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Recent Transactions</h4>
            {account.transactions?.length > 0 ? (
              <div className="border border-gray-200 max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {account.transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="px-4 py-2">{formatDate(tx.created_at)}</td>
                        <td className="px-4 py-2">{capitalizeFirst(tx.type)}</td>
                        <td className={`px-4 py-2 text-right font-medium ${tx.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </td>
                        <td className="px-4 py-2 text-right">{formatCurrency(tx.balance_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No transactions yet</p>
            )}
          </div>
        </div>

        {/* Transaction Dialog */}
        <Dialog open={!!showTransaction} onOpenChange={() => setShowTransaction(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{showTransaction === 'deposit' ? 'Make Deposit' : 'Make Withdrawal'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount (ZAR)</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="transaction-amount-input"
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <select
                  className="w-full h-9 border border-gray-200 rounded px-3 text-sm"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  data-testid="payment-type-select"
                >
                  <option value="cash">Cash</option>
                  <option value="eft">EFT / Electronic Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="internal_transfer">Internal Transfer</option>
                  {paymentTypes.map(pt => (
                    <option key={pt.id} value={pt.name?.toLowerCase()}>{pt.name}</option>
                  ))}
                </select>
              </div>
              {showTransaction === 'withdraw' && (
                <p className="text-sm text-gray-500">
                  Available Balance: {formatCurrency(account.balance)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransaction(null)}>Cancel</Button>
              <Button
                onClick={() => handleTransaction(showTransaction)}
                disabled={transactionLoading}
                className={showTransaction === 'deposit' ? 'bg-emerald-600' : 'bg-red-600'}
                data-testid="confirm-transaction"
              >
                {transactionLoading ? 'Processing...' : (showTransaction === 'deposit' ? 'Deposit' : 'Withdraw')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transfer Dialog */}
        <Dialog open={!!showTransfer} onOpenChange={() => setShowTransfer(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-[#C5A059]" />
                {showTransfer === 'internal' ? 'Internal Transfer' : 'External Transfer'}
              </DialogTitle>
            </DialogHeader>

            <Tabs value={showTransfer || 'internal'} onValueChange={(v) => setShowTransfer(v)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="internal">Internal (CBS)</TabsTrigger>
                <TabsTrigger value="external">External (Other Bank)</TabsTrigger>
              </TabsList>

              <TabsContent value="internal" className="space-y-4 pt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">From Account</Label>
                  <Input value={`${account.account_number} - ${formatCurrency(account.balance)}`} disabled className="bg-gray-50" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">To Account *</Label>
                  <Select value={transferData.to_account_id} onValueChange={(v) => setTransferData(p => ({...p, to_account_id: v}))}>
                    <SelectTrigger data-testid="transfer-to-account">
                      <SelectValue placeholder="Select destination account" />
                    </SelectTrigger>
                    <SelectContent>
                      {allAccounts.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.account_number} - {a.client_name} ({formatCurrency(a.balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Amount (ZAR) *</Label>
                  <Input
                    type="number"
                    value={transferData.amount}
                    onChange={(e) => setTransferData(p => ({...p, amount: e.target.value}))}
                    placeholder="0.00"
                    data-testid="transfer-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
                  <Input
                    value={transferData.description}
                    onChange={(e) => setTransferData(p => ({...p, description: e.target.value}))}
                    placeholder="Payment reference or note"
                    data-testid="transfer-description"
                  />
                </div>
              </TabsContent>

              <TabsContent value="external" className="space-y-4 pt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">From Account</Label>
                  <Input value={`${account.account_number} - ${formatCurrency(account.balance)}`} disabled className="bg-gray-50" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Beneficiary Name *</Label>
                    <Input
                      value={transferData.beneficiary_name}
                      onChange={(e) => setTransferData(p => ({...p, beneficiary_name: e.target.value}))}
                      data-testid="beneficiary-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Bank Name *</Label>
                    <Select value={transferData.beneficiary_bank} onValueChange={(v) => setTransferData(p => ({...p, beneficiary_bank: v}))}>
                      <SelectTrigger data-testid="beneficiary-bank">
                        <SelectValue placeholder="Select bank" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ABSA">ABSA</SelectItem>
                        <SelectItem value="FNB">FNB (First National Bank)</SelectItem>
                        <SelectItem value="Standard Bank">Standard Bank</SelectItem>
                        <SelectItem value="Nedbank">Nedbank</SelectItem>
                        <SelectItem value="Capitec">Capitec</SelectItem>
                        <SelectItem value="Investec">Investec</SelectItem>
                        <SelectItem value="African Bank">African Bank</SelectItem>
                        <SelectItem value="TymeBank">TymeBank</SelectItem>
                        <SelectItem value="Discovery Bank">Discovery Bank</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Account Number *</Label>
                    <Input
                      value={transferData.beneficiary_account_number}
                      onChange={(e) => setTransferData(p => ({...p, beneficiary_account_number: e.target.value}))}
                      data-testid="beneficiary-account"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Branch Code</Label>
                    <Input
                      value={transferData.beneficiary_branch_code}
                      onChange={(e) => setTransferData(p => ({...p, beneficiary_branch_code: e.target.value}))}
                      data-testid="beneficiary-branch"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Amount (ZAR) *</Label>
                  <Input
                    type="number"
                    value={transferData.amount}
                    onChange={(e) => setTransferData(p => ({...p, amount: e.target.value}))}
                    placeholder="0.00"
                    data-testid="external-transfer-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
                  <Input
                    value={transferData.description}
                    onChange={(e) => setTransferData(p => ({...p, description: e.target.value}))}
                    placeholder="Payment reference"
                  />
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 rounded" data-testid="external-transfer-notice">
                  External transfers are recorded as pending. Payment gateway integration required to process actual bank transfers.
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransfer(null)}>Cancel</Button>
              <Button
                onClick={handleTransfer}
                disabled={transferLoading}
                className="bg-[#1f1f1f] hover:bg-[#333333]"
                data-testid="confirm-transfer"
              >
                {transferLoading ? 'Processing...' : (showTransfer === 'internal' ? 'Transfer Now' : 'Submit Transfer')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default SavingsPage;
